import React, { useState } from 'react';

import { Button, Flex } from '@opendoor/bricks/core';

import { ContractDetailsModule } from 'buying/components/buyDirectCheckout/ContractDetailsModule';
import { BuyDirectCheckoutFlowContext } from 'buying/stateMachines/buyDirectCheckoutFlowStateMachine';

interface IContractDetailsProps {
  buyDirectDiscount: number;
  listPrice: number;
  state: {
    context: BuyDirectCheckoutFlowContext;
  };
  send: (args: string | { type: string; value: string | boolean | number }) => void;
}

const PRIMARY_BUYER_KEY = 0;
const COBUYER_KEY = 1;
/**
 * ContractDetails step
 * storybook-check-ignore
 */
export const ContractDetails: React.FC<IContractDetailsProps> = React.memo(
  ({ buyDirectDiscount, listPrice, send, state }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const handleOnCreateContract = () => {
      setIsSubmitting(true);
      send('FORWARD');
    };

    const handleOnBack = () => {
      window.scrollTo(0, 0);
      send('BACK');
    };

    const contactDetails = state?.context?.contactDetails?.[PRIMARY_BUYER_KEY];
    const coBuyerContactDetails = state?.context?.contactDetails?.[COBUYER_KEY];
    const buyerDetails = {
      name: contactDetails?.name ?? 'No name provided',
      email: contactDetails?.email ?? 'No email provided',
      phone: contactDetails?.phone ?? 'No phone provided',
    };
    const coBuyerDetails = coBuyerContactDetails
      ? {
          name: coBuyerContactDetails?.name ?? 'No name provided',
          email: coBuyerContactDetails?.email ?? 'No email provided',
          phone: coBuyerContactDetails?.phone ?? 'No phone provided',
        }
      : undefined;

    const downPaymentAmount = state?.context?.mortgageDownPaymentAmount;
    const downPaymentPercentage = state?.context?.mortgageDownPaymentPercentage;
    const loanAmount = state?.context?.loanAmount;
    const closeOfEscrow = state?.context?.closeDate ?? 'No close date provided';
    const loanType = state?.context?.fundingSource;
    const preApprovalDoc = state?.context?.mortgagePreapproval;

    let fundingSource = 'Conventional loan';
    switch (loanType) {
      case 'CONVENTIONAL':
        fundingSource = 'Conventional loan';
        break;
      case 'VA':
        fundingSource = 'VA loan';
        break;
      case 'FHA':
        fundingSource = 'FHA loan';
        break;
      case 'CASH':
        fundingSource = 'Cash';
        break;
      case 'USDA':
        fundingSource = 'USDA loan';
        break;
      default:
        fundingSource = 'Conventional loan';
        break;
    }

    // TODO: Add support for pre-approval doc when capability is available
    return (
      <Flex flexDirection="column" width="100%">
        <ContractDetailsModule
          buyDirectDiscount={buyDirectDiscount}
          buyerDetails={buyerDetails}
          coBuyerDetails={coBuyerDetails}
          closeOfEscrow={closeOfEscrow}
          downPaymentAmount={downPaymentAmount}
          downPaymentPercentage={downPaymentPercentage}
          fundingSource={fundingSource}
          listPrice={listPrice}
          loanAmount={loanAmount}
          preApprovalDoc={preApprovalDoc}
        />
        <Flex
          flexDirection={['column', null, 'row-reverse']}
          alignItems="center"
          justifyContent="end"
          mt={[4, null, 8]}
          width="100%"
        >
          <Button
            alignSelf={['center', null, 'flex-end']}
            analyticsName="buy-direct-checkout-create-contract"
            aria-label="Get started"
            data-testid="buy-direct-checkout-create-contract-button"
            disabled={isSubmitting}
            loading={isSubmitting}
            width={['100%', '100%', '100%', '40%']}
            variant="primary"
            onClick={handleOnCreateContract}
            mt={8}
          >
            Create contract
          </Button>
          <Button
            analyticsName="buy-direct-checkout-contract-details-back"
            aria-label="Back"
            data-testid="buy-direct-checkout-contract-details-back-button"
            width={['100%', '100%', '100%', '25%']}
            variant="secondary"
            onClick={handleOnBack}
            mt={8}
            mr={[null, null, 4]}
          >
            Back
          </Button>
        </Flex>
      </Flex>
    );
  },
);

ContractDetails.displayName = 'ContractDetails';
